/**
 * PaywallModal - Premium upgrade gate wrapper
 *
 * Purpose: Wraps branded PremiumPaywall component in modal overlay
 * Triggers: Any gated feature tap by free user
 */

import { PremiumPaywall } from '@/components/brand';
import { BRAND_IMAGES } from '@/styles/tokens';
import { useSubscription } from '@/hooks';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PaywallModal({ isOpen, onClose }: PaywallModalProps) {
  const { subscribe } = useSubscription();

  const handleUpgrade = async () => {
    try {
      // Start Stripe Checkout (redirects to Stripe)
      await subscribe();
      onClose();
    } catch (error) {
      console.error('Failed to start checkout:', error);
      // Keep modal open on error so user can retry
    }
  };

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Paywall card */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 380 }}>
        <PremiumPaywall isOpen={isOpen} onClose={onClose} onUpgrade={handleUpgrade} />
      </div>
    </div>
  );
}

export default PaywallModal;
